import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { testUsersSchema } from '../models/schema'
import { TestUser } from '../models/TestUser'
import {
  createTestUser as createTestUserAPI,
  deleteTestUser as deleteTestUserAPI,
  fetchTestUserById,
  fetchTestUsers,
} from '../services/TestUserService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<TestUser> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'testUser',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getTestUsersSuccess,
  setQuery: setTestUserQuery,
  reset: resetTestUserState,
  resetQueryAndIds: resetTestUserQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: TestUser }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectTestUsers = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedTestUser = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectTestUserById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectTestUsersByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedTestUser =
  (testUser: TestUser | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<TestUser>(testUser)))

export const getTestUsers = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchTestUsers(params),
    [testUsersSchema],
    slice.actions
  )

export const createTestUser = (testUser: TestUser) =>
  SBAPI.CreateDispatched(
    createTestUserAPI(testUser),
    testUsersSchema,
    slice.actions
  )

export const getTestUserWithId = (id: number) =>
  SBAPI.FetchDispatched<TestUser>(
    fetchTestUserById(id),
    testUsersSchema,
    slice.actions
  )

export const deleteTestUser = (testUser: TestUser) =>
  SBAPI.DestroyDispatched<TestUser>(
    deleteTestUserAPI(testUser),
    testUser,
    slice.actions
  )
